// src/shared/components/layout/Header.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { handleSectionNavigation } from '../../../utils/scrollHandler';
import { Menu, X, ShoppingBag } from 'lucide-react';
import { useCart } from '../../../context/CartContext';
import logo from '../../../assets/images/logo/pontso-yarona-logo.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { cartSummary } = useCart();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const navigation = [
    { name: 'Kganya Scents', href: '/kganya-scents', type: 'route' },
    { name: 'Farming',        href: '/farming',        type: 'route' },
    { name: 'Films',          href: '/films',          type: 'route' },
    { name: 'About',          href: '#about',          type: 'anchor' },
  ];

  const itemCount = cartSummary?.itemCount ?? 0;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-brand-black/97 backdrop-blur-2xl border-b border-white/5 shadow-[0_1px_40px_rgba(0,0,0,0.6)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-[72px]">

          {/* ── Logo ─────────────────────────────────────────── */}
          <Link
            to="/"
            className="flex items-center gap-3 group flex-shrink-0"
            onClick={() => setIsMenuOpen(false)}
          >
            <img
              src={logo}
              alt="Pontso Yarona"
              className="h-9 sm:h-11 w-auto transition-transform duration-300 group-hover:scale-105"
            />
            <div className="hidden sm:block leading-none">
              <p className="text-[15px] font-heading font-medium text-brand-gold tracking-wide">
                Pontso Yarona
              </p>
              <p className="text-[10px] text-brand-light/40 font-body uppercase tracking-[0.2em] mt-0.5">
                Empires of the mind
              </p>
            </div>
          </Link>

          {/* ── Desktop Nav ───────────────────────────────────── */}
          <nav className="hidden lg:flex items-center gap-8">
            {navigation.map((item) =>
              item.type === 'route' ? (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative text-sm font-body tracking-wide pb-0.5 transition-colors duration-200 ${
                    location.pathname === item.href
                      ? 'text-brand-gold'
                      : 'text-brand-light/60 hover:text-brand-light'
                  }`}
                >
                  {item.name}
                  {location.pathname === item.href && (
                    <span className="absolute -bottom-0.5 left-0 w-full h-px bg-brand-gold/70 rounded-full" />
                  )}
                </Link>
              ) : (
                <button
                  key={item.name}
                  onClick={() => handleSectionNavigation(item.href, navigate)}
                  className="text-sm font-body tracking-wide text-brand-light/60 hover:text-brand-light transition-colors duration-200"
                >
                  {item.name}
                </button>
              )
            )}
          </nav>

          {/* ── Actions ───────────────────────────────────────── */}
          <div className="flex items-center gap-3">
            {/* Cart */}
            <Link
              to="/checkout"
              aria-label="Cart"
              className="relative flex items-center justify-center w-10 h-10 rounded-xl border border-white/10 hover:border-brand-gold/40 hover:bg-brand-gold/5 transition-all duration-200"
            >
              <ShoppingBag className="w-5 h-5 text-brand-light/70 group-hover:text-brand-gold" />
              {itemCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-brand-red text-white rounded-full flex items-center justify-center text-[10px] font-bold font-body leading-none">
                  {itemCount > 9 ? '9+' : itemCount}
                </span>
              )}
            </Link>

            {/* Mobile hamburger */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-xl border border-white/10 hover:border-brand-gold/40 transition-all duration-200 text-brand-light/70 hover:text-brand-gold"
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile Drawer ─────────────────────────────────────── */}
      {isMenuOpen && (
        <div className="lg:hidden bg-brand-black/98 backdrop-blur-2xl border-t border-white/5">
          <nav className="max-w-7xl mx-auto px-5 py-6 flex flex-col gap-1">
            {navigation.map((item) =>
              item.type === 'route' ? (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-4 py-3.5 rounded-xl text-base font-body tracking-wide transition-all duration-200 ${
                    location.pathname === item.href
                      ? 'bg-brand-gold/10 text-brand-gold border border-brand-gold/20'
                      : 'text-brand-light/70 hover:text-brand-light hover:bg-white/5 border border-transparent'
                  }`}
                >
                  {item.name}
                </Link>
              ) : (
                <button
                  key={item.name}
                  onClick={() => { handleSectionNavigation(item.href, navigate); setIsMenuOpen(false); }}
                  className="px-4 py-3.5 rounded-xl text-base font-body tracking-wide text-brand-light/70 hover:text-brand-light hover:bg-white/5 transition-all duration-200 text-left border border-transparent"
                >
                  {item.name}
                </button>
              )
            )}

            {/* Cart link in mobile menu */}
            <Link
              to="/checkout"
              onClick={() => setIsMenuOpen(false)}
              className="mt-3 flex items-center gap-3 px-4 py-3.5 rounded-xl bg-brand-red/10 border border-brand-red/20 text-brand-light/80 hover:text-brand-light transition-all duration-200"
            >
              <ShoppingBag className="w-5 h-5 text-brand-red/80" />
              <span className="font-body text-base">Cart{itemCount > 0 ? ` (${itemCount})` : ''}</span>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
